/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import routesFac from "routesfac";
import routesFiscal from "routesfiscal";
import routesCont from "routescont";
import routesNom from "routesnom";
import { registrarVisita, catalogoDesdeRutas } from "lib/usoPantallas";

// Material Dashboard 2 React contexts
import { useMaterialUIController, GlobalMDAlertProvider, setModuloActivo } from "context";

// Images
import Detalleexpediente from "modules/prestamos/detalleexpediente";
import DetalleIncauto from "modules/motores/incautos";
import DetalleQrexpediente from "modules/prestamos/qrexpediente";
import ExpedienteDetalle from "modules/prestamos/expedientedetalle";
import ClienteDetalle from "modules/clientes/clientedetalle";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import SimpleSidenav from "modules/menu/SimpleSidenav";
import SidebarFac from "modules/menu/SidebarFac";
import SidebarNom from "modules/menu/SidebarNom";
import ModulosCliente from "modules/menu/PantallaModulosCliente";
import SesionPersistente from "modules/menu/SesionPersistente";
import { EmpresaFiscalProvider, ClaveEmpresaFiscal } from "lib/empresaFiscal";
import HistorialChasis from "modules/motores/historialchasis";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    moduloActivo,
    usuarioLog,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const pathnameSegments = pathname.split("/").filter(Boolean);
  // Ruta QR pública (/:negocio/:id/:data) — sin menú. Las pantallas internas
  // con 3 segmentos (p.ej. /nomina/configuracion/catalogos) se excluyen
  // por el primer segmento del módulo para que conserven el menú.
  const isDetalleQrexpedienteRoute =
    pathnameSegments.length === 3 &&
    ![
      "expediente",
      "incauto",
      "prestamos",
      "inventario",
      "clientes",
      "nomina",
      "contabilidad",
      "facturacion",
      "fiscal",
    ].includes(pathnameSegments[0]);
  // El menú lateral se muestra en las pantallas internas (no en login/QR).
  const hasSidebar =
    pathname !== "/login" && pathname !== "/moduloscliente" && !isDetalleQrexpedienteRoute;

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Chrome cambia el valor de un <input type="number"> enfocado con la rueda
  // del mouse: el operador escribe 2500, desplaza la página con la rueda
  // sobre el campo y el monto queda en 2498 sin que lo note (recibo 25752 de
  // Gerald San Cristóbal). Al rodar sobre el campo enfocado se le quita el
  // foco antes de que el navegador aplique el incremento; la rueda vuelve a
  // desplazar la página y el valor escrito se conserva. Aplica a toda la app.
  useEffect(() => {
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number" && el === e.target) el.blur();
    };
    document.addEventListener("wheel", onWheel, { passive: true, capture: true });
    return () => document.removeEventListener("wheel", onWheel, { capture: true });
  }, []);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Limpia el módulo activo cuando el usuario regresa al login o a la
  // pantalla de selección de módulos, para que la próxima entrada elija
  // explícitamente desde `PantallaModulosCliente`.
  useEffect(() => {
    if (pathname === "/login" || pathname === "/moduloscliente") {
      if (moduloActivo) setModuloActivo(dispatch, null);
    }
  }, [pathname, moduloActivo, dispatch]);

  // Construye el listado de <Route /> a partir del arreglo de rutas. Se devuelve
  // SIEMPRE un arreglo plano (sin anidaciones) para que `<Routes>` registre
  // correctamente cada ruta hija de los grupos `collapse` (en React Router v6
  // los hijos deben ser elementos `<Route />` directos).
  const getRoutes = (allRoutes) => {
    const flat = [];
    (allRoutes || []).forEach((route) => {
      if (Array.isArray(route?.collapse)) {
        flat.push(...getRoutes(route.collapse));
        return;
      }
      if (route?.route) {
        flat.push(
          <Route path={route.route} element={route.component} key={route.key} />
        );
      }
    });
    return flat;
  };

  // Aplana un arreglo de rutas (estilo `routes.js`) y devuelve únicamente los
  // paths navegables. Se usa para deducir qué rutas pertenecen exclusivamente
  // al módulo de Facturación y así activar el sidebar correcto.
  const aplanarRutas = (lista) => {
    const out = [];
    (lista || []).forEach((r) => {
      if (Array.isArray(r?.collapse)) out.push(...aplanarRutas(r.collapse));
      if (r?.route) out.push(r.route);
    });
    return out;
  };

  // Prefijos de primer nivel (`/algo`) que aparecen sólo en `routesfac.js` y
  // no en `routes.js`. Se usan únicamente como FALLBACK cuando no hay un
  // `moduloActivo` definido en contexto (por ejemplo si el usuario abre una
  // URL directa de Facturación sin pasar por la pantalla de selección).
  const prefijosFacturacion = useMemo(() => {
    const rutasNormales = new Set(aplanarRutas(routes));
    const prefijos = new Set();
    aplanarRutas(routesFac).forEach((ruta) => {
      if (!ruta || ruta === "/login" || rutasNormales.has(ruta)) return;
      const segmento = "/" + ruta.split("/").filter(Boolean)[0];
      if (segmento && segmento !== "/" && segmento !== "/login") {
        prefijos.add(segmento);
      }
    });
    return [...prefijos];
  }, []);

  const prefijosContabilidad = useMemo(() => {
    const rutasNormales = new Set(aplanarRutas(routes));
    const prefijos = new Set();
    aplanarRutas(routesCont).forEach((ruta) => {
      if (!ruta || ruta === "/login" || rutasNormales.has(ruta)) return;
      const segmento = "/" + ruta.split("/").filter(Boolean)[0];
      if (segmento && segmento !== "/" && segmento !== "/login") {
        prefijos.add(segmento);
      }
    });
    return [...prefijos];
  }, []);

  // Decide en qué módulo está el usuario. Prioridad: estado en contexto
  // (`moduloActivo`, fijado por `PantallaModulosCliente` y persistido en
  // localStorage) → fallback por prefijo de URL.
  const enFiscal = useMemo(() => {
    if (moduloActivo === "gestionfiscal" || moduloActivo === "fiscal") return true;
    return pathname.startsWith("/fiscal");
  }, [moduloActivo, pathname]);

  const enNomina = useMemo(() => {
    if (enFiscal) return false;
    if (moduloActivo === "nomina") return true;
    if (
      moduloActivo === "facturacion" ||
      moduloActivo === "prestamos" ||
      moduloActivo === "contabilidad" ||
      moduloActivo === "cuentasxpagar"
    ) {
      return false;
    }
    return pathname.startsWith("/nomina");
  }, [moduloActivo, pathname, enFiscal]);

  const enContabilidad = useMemo(() => {
    if (enFiscal || enNomina) return false;
    if (moduloActivo === "contabilidad" || moduloActivo === "cuentasxpagar") return true;
    if (moduloActivo === "facturacion" || moduloActivo === "prestamos") return false;
    return (
      pathname.startsWith("/contabilidad") ||
      prefijosContabilidad.some((p) => pathname.startsWith(p))
    );
  }, [moduloActivo, pathname, prefijosContabilidad, enFiscal, enNomina]);

  const enFacturacion = useMemo(() => {
    if (enFiscal || enContabilidad || enNomina) return false;
    if (moduloActivo === "facturacion") return true;
    if (moduloActivo === "prestamos") return false;
    return prefijosFacturacion.some((p) => pathname.startsWith(p));
  }, [moduloActivo, pathname, prefijosFacturacion, enFiscal, enContabilidad, enNomina]);

  // Si llegamos por URL directa a una ruta exclusiva de Facturación, Fiscal,
  // Contabilidad o Nómina sin tener `moduloActivo` definido, lo registramos
  // para que persista en recargas y en navegaciones a rutas compartidas.
  useEffect(() => {
    if (!moduloActivo && pathname !== "/login" && pathname !== "/moduloscliente") {
      if (enFiscal) setModuloActivo(dispatch, "gestionfiscal");
      else if (enNomina) setModuloActivo(dispatch, "nomina");
      else if (enContabilidad) setModuloActivo(dispatch, "contabilidad");
      else if (enFacturacion) setModuloActivo(dispatch, "facturacion");
    }
  }, [moduloActivo, enFiscal, enNomina, enContabilidad, enFacturacion, pathname, dispatch]);

  const rutasActivas = enFiscal
    ? routesFiscal
    : enNomina
      ? routesNom
      : enContabilidad
        ? routesCont
        : enFacturacion
          ? routesFac
          : routes;

  // Registro de uso de pantallas para el inicio personalizado. Solo rutas
  // estáticas del módulo principal (las paramétricas y las de otros módulos
  // no se pueden ofrecer como acceso directo desde el Inicio). Sin usuario en
  // sesión no se registra: las tablets se comparten y el uso es por persona.
  const catalogoPantallas = useMemo(() => catalogoDesdeRutas(routes), []);
  useEffect(() => {
    if (enFiscal || enFacturacion || enNomina || pathname === "/dashboard") return;
    registrarVisita(usuarioLog, catalogoPantallas.get(pathname));
  }, [pathname, usuarioLog, enFiscal, enFacturacion, enNomina, catalogoPantallas]);

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* En desktop, cuando hay menú lateral, los diálogos a pantalla completa
          (formularios tipo "Venta a crédito", listados unificados, etc.) se
          desplazan 264px para no tapar el menú: queda visible y clickeable.
          Requiere CSS :has() (Chrome 105+); si no, el diálogo cubre todo como antes. */}
      {hasSidebar && (
        <GlobalStyles
          styles={{
            "@media (min-width:900px)": {
              ".MuiDialog-root:has(.MuiDialog-paperFullScreen)": { left: "264px !important" },
              ".MuiDialog-root:has(.MuiDialog-paperFullScreen) .MuiBackdrop-root": {
                left: "264px !important",
              },
            },
          }}
        />
      )}
      <GlobalMDAlertProvider>
        <SesionPersistente>
        <EmpresaFiscalProvider enFiscal={enFiscal}>
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
          {hasSidebar &&
            (enNomina ? (
              <SidebarNom routes={rutasActivas} tituloModulo="Nómina" />
            ) : enFacturacion || enContabilidad ? (
              <SidebarFac
                routes={rutasActivas}
                tituloModulo={enContabilidad ? "Contabilidad" : "Facturación"}
                programaModulo={enContabilidad ? "contabilidad" : ""}
              />
            ) : (
              <SimpleSidenav routes={rutasActivas} selectorEmpresaFiscal={enFiscal} />
            ))}

          {/* minWidth: 0 — sin él, un flex item no encoge por debajo del ancho
              intrínseco de su contenido (p.ej. un DataGrid de ~1700px de
              columnas) y la página entera desborda el viewport. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Cambiar de empresa fiscal remonta la pantalla: recarga
                secuencias, listados y certificado de la empresa elegida. */}
            <ClaveEmpresaFiscal>
            <Routes>
              {getRoutes(rutasActivas)}
              <Route
                exact
                path="/prestamos/expediente/:noprest"
                element={<ExpedienteDetalle />}
                key="expedientedetalle"
              />
              <Route
                exact
                path="/clientes/expediente/:cedula"
                element={<ClienteDetalle />}
                key="clientedetalle"
              />
              <Route
                exact
                path="/inventario/historialchasis/:chasis"
                element={<HistorialChasis />}
                key="historialchasisdetalle"
              />
              <Route
                exact
                path="/moduloscliente"
                element={<ModulosCliente />}
                key="moduloscliente"
              />
              <Route
                exact
                path={"/:nombrenegocio/:id/:data"}
                element={<DetalleQrexpediente />}
                key="detalleQrexpedientes"
              />
              <Route
                exact
                path={"/expediente/:id/:data"}
                element={<Detalleexpediente />}
                key="detalleexpedientes"
              />

              <Route
                exact
                path={"/incauto/:id/:data"}
                element={<DetalleIncauto />}
                key="detalleincauto"
              />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
            </ClaveEmpresaFiscal>
          </Box>
        </Box>
        </EmpresaFiscalProvider>
        </SesionPersistente>
      </GlobalMDAlertProvider>
    </ThemeProvider>
  );
}
